import { Injectable } from '@angular/core';
import type { MarcoCronograma, PrazoExercicio, UrgenciaPrazo } from '../../home/models/home-dashboard.model';
import type {
  DiaCalendario,
  MarcoInstitucional,
  MesCalendarioView,
} from '../models/portfolio-cronograma.model';
import type { Portfolio, StatusPortfolio } from './portfolio-api.service';

export const ROTULOS_STATUS_PORTFOLIO: Record<StatusPortfolio, string> = {
  RASCUNHO: 'Rascunho',
  EM_PRIORIZACAO: 'Em priorização',
  EM_APROVACAO_EM: 'Em aprovação no EM',
  APROVADO_CHEFE_EM: 'Aprovado pelo Chefe do EM',
  PUBLICADO_BCG: 'Publicado no BCG',
  ARQUIVADO: 'Arquivado',
};

export type PortfolioCronogramaInput = Pick<
  Portfolio,
  'id' | 'nome' | 'anoExecucao' | 'status' | 'dataLimiteConsolidacao'
>;

function diasAte(dataIso: string): number {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(dataIso);
  alvo.setHours(0, 0, 0, 0);
  return Math.ceil((alvo.getTime() - hoje.getTime()) / 86_400_000);
}

const MESES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function iso(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

function calcularLimiteConsolidacao(anoExecucao: number): string {
  return iso(anoExecucao - 1, 4, 30);
}

function urgenciaDeDias(dias: number): UrgenciaPrazo {
  if (dias < 0) {
    return 'VENCIDO';
  }
  if (dias <= 7) {
    return 'CRITICO';
  }
  if (dias <= 30) {
    return 'ATENCAO';
  }
  return 'NO_PRAZO';
}

@Injectable({ providedIn: 'root' })
export class PortfolioCronogramaService {
  montarPrazoExercicio(portfolio: PortfolioCronogramaInput | null): PrazoExercicio | null {
    if (!portfolio?.dataLimiteConsolidacao || !portfolio.anoExecucao) {
      return null;
    }
    const diasRestantes = diasAte(portfolio.dataLimiteConsolidacao);
    const inicio = new Date(portfolio.anoExecucao, 0, 1);
    const fim = new Date(portfolio.dataLimiteConsolidacao);
    const hoje = new Date();
    const totalMs = fim.getTime() - inicio.getTime();
    const decorridoMs = hoje.getTime() - inicio.getTime();
    const percentualDecorrido =
      totalMs > 0 ? Math.min(100, Math.max(0, Math.round((decorridoMs / totalMs) * 100))) : 0;

    return {
      nomePortfolio: portfolio.nome ?? 'Portfólio',
      anoExecucao: portfolio.anoExecucao,
      dataLimiteConsolidacao: portfolio.dataLimiteConsolidacao,
      diasRestantes,
      percentualDecorrido,
      urgencia: urgenciaDeDias(diasRestantes),
      statusRotulo: ROTULOS_STATUS_PORTFOLIO[portfolio.status] ?? portfolio.status,
    };
  }

  montarCronograma(portfolio: PortfolioCronogramaInput | null): MarcoCronograma[] {
    if (!portfolio?.anoExecucao) {
      return [];
    }
    const ano = portfolio.anoExecucao;
    const ordemStatus: StatusPortfolio[] = [
      'RASCUNHO',
      'EM_PRIORIZACAO',
      'EM_APROVACAO_EM',
      'APROVADO_CHEFE_EM',
      'PUBLICADO_BCG',
      'ARQUIVADO',
    ];
    const idxAtual = ordemStatus.indexOf(portfolio.status ?? 'RASCUNHO');

    const defs: { rotulo: string; data?: string; step: number }[] = [
      { rotulo: 'Abertura do exercício', data: `${ano}-01-01`, step: -1 },
      {
        rotulo: 'Consolidação de demandas',
        data: portfolio.dataLimiteConsolidacao,
        step: 0,
      },
      { rotulo: 'Priorização multicritério', step: 1 },
      { rotulo: 'Aprovação no Estado-Maior', step: 2 },
      { rotulo: 'Aprovação do Chefe do EM', step: 3 },
      { rotulo: 'Publicação no BCG', step: 4 },
    ];

    if (portfolio.status === 'ARQUIVADO') {
      return defs.map((d) => ({
        rotulo: d.rotulo,
        data: d.data,
        status: 'CONCLUIDO' as const,
      }));
    }

    return defs.map((d) => ({
      rotulo: d.rotulo,
      data: d.data,
      status:
        d.step < 0
          ? ('CONCLUIDO' as const)
          : idxAtual > d.step
            ? ('CONCLUIDO' as const)
            : idxAtual === d.step
              ? ('ATUAL' as const)
              : ('PENDENTE' as const),
    }));
  }

  rotuloStatus(status: StatusPortfolio): string {
    return ROTULOS_STATUS_PORTFOLIO[status] ?? status;
  }

  /**
   * Marcos institucionais com datas sugeridas conforme Portaria PMDF nº 990/2015 e fluxo MG3P.
   * Obrigatório: Art. 5º (consolidação até 30/04 do ano anterior à execução).
   */
  sugerirMarcosInstitucionais(portfolio: PortfolioCronogramaInput | null): MarcoInstitucional[] {
    if (!portfolio?.anoExecucao) {
      return [];
    }
    const exe = portfolio.anoExecucao;
    const ref = exe - 1;
    const limite = portfolio.dataLimiteConsolidacao ?? calcularLimiteConsolidacao(exe);

    const marcos: MarcoInstitucional[] = [
      {
        id: 'abertura-exercicio',
        rotulo: 'Abertura do exercício',
        data: iso(exe, 1, 1),
        artigo: 'Art. 1º e 5º',
        fundamentacao: 'Início do ciclo de gestão do portfólio do ano de execução.',
        natureza: 'SUGERIDO',
        faseWorkflow: -1,
      },
      {
        id: 'consolidacao-demandas',
        rotulo: 'Limite de consolidação de demandas',
        data: limite,
        artigo: 'Art. 5º',
        fundamentacao:
          'Portfólio anual consolidado até o último dia de abril do ano anterior ao de execução (RN-PTF-001).',
        natureza: 'OBRIGATORIO',
        faseWorkflow: 0,
      },
      {
        id: 'priorizacao',
        rotulo: 'Priorização multicritério',
        data: iso(ref, 5, 31),
        artigo: 'Art. 21 e 22',
        fundamentacao: 'Janela sugerida após consolidação para ranqueamento das iniciativas.',
        natureza: 'SUGERIDO',
        faseWorkflow: 1,
      },
      {
        id: 'comite-governanca',
        rotulo: 'Deliberação do comitê de governança',
        data: iso(ref, 6, 15),
        artigo: 'Art. 6º',
        fundamentacao: 'Decisões estratégicas colegiadas antes da submissão ao Estado-Maior.',
        natureza: 'SUGERIDO',
        faseWorkflow: 1,
      },
      {
        id: 'submissao-em',
        rotulo: 'Submissão ao Estado-Maior',
        data: iso(ref, 7, 31),
        artigo: 'Art. 4º',
        fundamentacao: 'Seção de Projetos recebe o portfólio para análise e aprovação institucional.',
        natureza: 'SUGERIDO',
        faseWorkflow: 2,
      },
      {
        id: 'aprovacao-chefe',
        rotulo: 'Aprovação do Chefe do EM',
        data: iso(ref, 9, 30),
        artigo: 'Art. 4º e 6º',
        fundamentacao: 'Homologação da chefia do Estado-Maior antes da publicação oficial.',
        natureza: 'SUGERIDO',
        faseWorkflow: 3,
      },
      {
        id: 'publicacao-bcg',
        rotulo: 'Publicação no BCG',
        data: iso(ref, 12, 31),
        artigo: 'Art. 5º e 37, V',
        fundamentacao: 'Registro da publicação oficial do portfólio aprovado (RN-PTF-006).',
        natureza: 'OBRIGATORIO',
        faseWorkflow: 4,
      },
      {
        id: 'prestacao-contas',
        rotulo: 'Prestação de contas (subportfólios)',
        data: iso(exe, 6, 30),
        artigo: 'Art. 7º e 8º',
        fundamentacao: 'Marco semestral sugerido para acompanhamento descentralizado.',
        natureza: 'SUGERIDO',
        faseWorkflow: -1,
      },
      {
        id: 'avaliacao-maturidade',
        rotulo: 'Avaliação anual de maturidade',
        data: iso(exe, 11, 30),
        artigo: 'Art. 33',
        fundamentacao: 'Ciclo de avaliação da maturidade em gestão de portfólio/programas/projetos.',
        natureza: 'SUGERIDO',
        faseWorkflow: -1,
      },
    ];
    return marcos.sort((a, b) => a.data.localeCompare(b.data));
  }

  marcosPorData(marcos: MarcoInstitucional[]): Map<string, MarcoInstitucional[]> {
    const mapa = new Map<string, MarcoInstitucional[]>();
    for (const m of marcos) {
      const lista = mapa.get(m.data) ?? [];
      lista.push(m);
      mapa.set(m.data, lista);
    }
    return mapa;
  }

  gerarMesCalendario(ano: number, mes: number, marcos: MarcoInstitucional[]): MesCalendarioView {
    const porData = this.marcosPorData(marcos);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const primeiro = new Date(ano, mes, 1);
    const offset = primeiro.getDay();
    const diasNoMes = new Date(ano, mes + 1, 0).getDate();
    const dias: DiaCalendario[] = [];

    for (let i = 0; i < offset; i++) {
      const d = new Date(ano, mes, -offset + i + 1);
      const data = iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
      dias.push({
        data,
        dia: d.getDate(),
        foraMes: true,
        hoje: false,
        marcos: porData.get(data) ?? [],
      });
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      const data = iso(ano, mes + 1, dia);
      const ref = new Date(ano, mes, dia);
      ref.setHours(0, 0, 0, 0);
      dias.push({
        data,
        dia,
        foraMes: false,
        hoje: ref.getTime() === hoje.getTime(),
        marcos: porData.get(data) ?? [],
      });
    }

    while (dias.length % 7 !== 0) {
      const ultimo = dias[dias.length - 1];
      const d = new Date(ultimo.data);
      d.setDate(d.getDate() + 1);
      const data = iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
      dias.push({
        data,
        dia: d.getDate(),
        foraMes: true,
        hoje: false,
        marcos: porData.get(data) ?? [],
      });
    }

    return {
      ano,
      mes,
      rotulo: `${MESES_PT[mes]} ${ano}`,
      dias,
    };
  }

  rotulosDiasSemana(): string[] {
    return DIAS_SEMANA;
  }

  mesInicialSugerido(marcos: MarcoInstitucional[]): { ano: number; mes: number } {
    const alvo =
      marcos.find((m) => m.natureza === 'OBRIGATORIO') ??
      marcos.find((m) => m.faseWorkflow >= 0) ??
      marcos[0];
    if (!alvo) {
      const hoje = new Date();
      return { ano: hoje.getFullYear(), mes: hoje.getMonth() };
    }
    const d = new Date(`${alvo.data}T12:00:00`);
    return { ano: d.getFullYear(), mes: d.getMonth() };
  }
}
